import { useEffect, useMemo, useState } from 'react';

import { useContentWithSSRProtection } from '@/hooks';
import { usePageProps } from '@/hooks/usePageProps';
// import { useGetContentQuery } from '@/queries';
import { getCurrentPageType } from '@/utils';

export const getPublisherMainData = (props) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Minfin',
  knowsLanguage: ['uk-UA', 'ru-UA'],
  description:
    'Актуальна інформація про фінанси в Україні: курси валют, фінансові новини, все про банки, ставки кредитів і депозитів, фінансова аналітика, котирування акцій, форекс.',
  url: 'https://minfin.com.ua/',
  logo: {
    '@type': 'ImageObject',
    url: 'https://minfin.com.ua/i/redesign/minfin-logo-ru.png',
  },
  foundingDate: '2008',
  sameAs: [
    'https://www.facebook.com/minfin.com.ua/',
    'https://t.me/Minfin_com_ua/',
    'https://www.youtube.com/channel/UCEW1QlFX_ito4gd3QXxyt5Q/',
    'https://twitter.com/MinfinComUa/',
    'https://www.instagram.com/minfin.com.ua/',
  ],
  address: {
    '@type': 'PostalAddress',
    addressCountry: {
      '@type': 'Country',
      name: 'UA',
    },
    addressLocality: 'Kyiv',
    postalCode: '03038',
    streetAddress: '4B Mykola Grinchenko Street',
  },
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+38(044)392-47-40',
      contactType: 'customer service',
      areaServed: 'UA',
      availableLanguage: ['Ukrainian', 'Russian'],
      email: 'info@minfin.com.ua',
    },
  ],
  ...props,
});

const defaultMeta = { h1: '', title: '', description: '', previewText: '' };

const getInitialProps = (props) => ({
  title: '',
  description: '',
  companyName: '',
  companyNameP: '',
  year: new Date().getFullYear(),
  ...props,
});

// export const useSeoMeta = (props) => {
//   const {
//     intl: { formatMessage, locale },
//     params,
//     location,
//   } = usePageProps();
//
//   // Визначаємо тип сторінки залежно від params
//   const pageType = getCurrentPageType({ params });
//
//   const { data = {}, status: contentStatus } = useGetContentQuery(
//     {
//       locale,
//       pathname: location.pathname,
//     },
//     { skip: !location.pathname || !locale }
//   );
//
//   const [metaData, setMetaData] = useState(data?.meta || {});
//
//   const intlProps = getInitialProps(props);
//
//   // Отримуємо SEO-мета контент для поточної сторінки
//   const { h1, title, description, previewText } = metaData;
//
//   // Форматуємо заголовки і мета-дані з урахуванням локалізації та типу сторінки
//   const formattedMetaTitle = formatMessage(
//     { id: `${pageType}.metaTitle` },
//     intlProps
//   );
//
//   const formattedMetaDescription = formatMessage(
//     { id: `${pageType}.metaDescription` },
//     intlProps
//   );
//
//   const formattedPageTitle = formatMessage(
//     { id: `${pageType}.title` },
//     intlProps
//   );
//
//   const formattedPreviewText = formatMessage(
//     { id: `${pageType}.description` },
//     intlProps
//   );
//
//   useEffect(() => {
//     if (contentStatus === 'fulfilled') {
//       setMetaData(data?.meta || {});
//     }
//
//     if (contentStatus === 'rejected') {
//       setMetaData(defaultMeta);
//     }
//   }, [location.pathname, contentStatus]);
//
//   return {
//     metaTitle: title || formattedMetaTitle,
//     metaDescription: description || formattedMetaDescription,
//     pageTitle: h1 || formattedPageTitle,
//     metaPreviewText: previewText || formattedPreviewText,
//   };
// };

export const formatSeoMeta = ({ params, formatMessage, props }) => {
  const pageType = getCurrentPageType({ params });
  const intlProps = getInitialProps(props);

  return {
    formattedMetaTitle: formatMessage(
      { id: `${pageType}.metaTitle` },
      intlProps
    ),
    formattedMetaDescription: formatMessage(
      { id: `${pageType}.metaDescription` },
      intlProps
    ),
    formattedPageTitle: formatMessage({ id: `${pageType}.title` }, intlProps),
    formattedPreviewText: formatMessage(
      { id: `${pageType}.description` },
      intlProps
    ),
  };
};

export const useSeoMeta = (props) => {
  const {
    intl: { formatMessage, locale },
    params,
    location,
  } = usePageProps();

  const { data, status: contentStatus } = useContentWithSSRProtection({
    locale,
    pathname: location.pathname,
  });

  const [metaData, setMetaData] = useState(data?.meta || defaultMeta);

  useEffect(() => {
    if (contentStatus === 'fulfilled') {
      setMetaData(data?.meta || {});
    } else if (contentStatus === 'rejected') {
      setMetaData(defaultMeta);
    }
  }, [contentStatus]);

  const isReady = contentStatus !== 'pending';

  const result = useMemo(() => {
    if (!isReady) {
      return {
        loading: true,
        metaTitle: '',
        metaDescription: '',
        pageTitle: '',
        metaPreviewText: '',
      };
    }

    const {
      formattedMetaTitle,
      formattedMetaDescription,
      formattedPageTitle,
      formattedPreviewText,
    } = formatSeoMeta({ params, formatMessage, props });

    const { h1, title, description, previewText } = metaData;

    return {
      loading: false,
      metaTitle: title || formattedMetaTitle,
      metaDescription: description || formattedMetaDescription,
      pageTitle: h1 || formattedPageTitle,
      metaPreviewText: previewText || formattedPreviewText,
    };
  }, [isReady, metaData]);

  return result;
};
